import { useTranslation } from 'react-i18next'
import MaterialIcon from '../ui/MaterialIcon'
import { KILO } from '../../constants/KILO_THEME'
import { usePredictiveCopy } from '../../hooks/usePredictiveCopy'
import { formatConflictTimeRange } from '../../lib/conflicts'
import type { StressEventContext } from '../../lib/predictiveSignals'

type StressEventPreviewProps = {
  context: StressEventContext
}

function barMetrics(
  startsAt: string,
  endsAt: string,
  windowStart: number,
  windowSpan: number,
): { left: number; width: number } {
  const start = new Date(startsAt).getTime()
  const end = new Date(endsAt).getTime()
  const left = ((start - windowStart) / windowSpan) * 100
  const width = ((end - start) / windowSpan) * 100
  return {
    left: Math.max(0, Math.min(100, left)),
    width: Math.max(2, Math.min(100 - left, width)),
  }
}

const TIER_ACCENT: Record<StressEventContext['stressTier'], string> = {
  high: 'bg-kilo-danger',
  medium: 'bg-amber-400',
  low: 'bg-kilo-soft',
}

const TIER_TRACK: Record<StressEventContext['stressTier'], string> = {
  high: 'bg-kilo-danger/20',
  medium: 'bg-amber-400/20',
  low: 'bg-kilo-line/40',
}

const StressEventPreview = ({ context }: StressEventPreviewProps) => {
  const { t } = useTranslation()
  const { formatEventCountdown, stressTierLabel } = usePredictiveCopy()

  const nowMs = Date.now()
  const eventStartMs = new Date(context.eventStart).getTime()
  const eventEndMs = new Date(context.eventEnd).getTime()
  const windowStart = nowMs
  const windowEnd = Math.max(eventEndMs, eventStartMs + 30 * 60 * 1000, nowMs + 60 * 60 * 1000)
  const windowSpan = Math.max(windowEnd - windowStart, 1)

  const eventBar = barMetrics(context.eventStart, context.eventEnd, windowStart, windowSpan)
  const nowLeft = 0

  return (
    <div className="mt-3 space-y-3 rounded-2xl border border-amber-900/40 bg-kilo-page/40 p-4">
      <div className="flex items-center justify-between gap-2">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-600">{t('predictive.preview.triggeringEvent')}</p>
        <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-amber-300">
          {formatEventCountdown(context.hoursUntilEvent)}
        </p>
      </div>

      <div className="flex items-start gap-3">
        <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl ${KILO.bg.card}`}>
          <MaterialIcon
            name={context.isWorkEvent ? 'work' : 'event'}
            filled
            size="sm"
          />
        </div>
        <div className="min-w-0 flex-1">
          <p className={`font-mono text-[10px] uppercase tracking-[0.15em] ${KILO.text.soft}`}>
            {context.isWorkEvent ? t('predictive.preview.workCalendar') : t('predictive.preview.calendar')}
          </p>
          <p className={`mt-0.5 font-semibold ${KILO.text.primary}`}>{context.eventTitle}</p>
          <p className={`mt-0.5 text-xs ${KILO.text.muted}`}>
            {formatConflictTimeRange(context.eventStart, context.eventEnd)}
          </p>
        </div>
      </div>

      <div className="space-y-1.5">
        <div className={`flex items-center justify-between text-[10px] uppercase tracking-[0.15em] ${KILO.text.soft}`}>
          <span>{t('predictive.preview.now')}</span>
          <span>{t('predictive.preview.eventWindow')}</span>
        </div>
        <div className={`relative h-2 overflow-hidden rounded-full ${TIER_TRACK[context.stressTier]}`}>
          <div
            className="absolute top-1/2 z-10 h-3 w-0.5 -translate-y-1/2 rounded-full bg-kilo-accent"
            style={{ left: `${nowLeft}%` }}
            aria-hidden
          />
          <div
            className={`absolute inset-y-0 rounded-full ${TIER_ACCENT[context.stressTier]}`}
            style={{ left: `${eventBar.left}%`, width: `${eventBar.width}%` }}
            aria-hidden
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <span className="rounded-lg bg-kilo-nested px-2 py-1 text-xs text-amber-200">
          {stressTierLabel(context.stressTier)}
        </span>
        <span className={`rounded-lg bg-kilo-nested px-2 py-1 text-xs ${KILO.text.primary}`}>
          {context.interventionMode === 'maintain'
            ? t('predictive.preview.keepTraining')
            : t('predictive.preview.protectEnergy')}
        </span>
        {context.expectedHrvDropPct != null && !context.learningApplied ? (
          <span className={`rounded-lg bg-kilo-nested px-2 py-1 text-xs ${KILO.text.primary}`}>
            {t('predictive.preview.populationPrior', { pct: Math.round(context.expectedHrvDropPct) })}
          </span>
        ) : null}
        {context.learningApplied && context.personalHrvImpactPct != null ? (
          <span className="rounded-lg bg-kilo-nested px-2 py-1 text-xs text-kilo-accent">
            {t('predictive.preview.yourAvgHrv', {
              pct: `${context.personalHrvImpactPct >= 0 ? '+' : ''}${context.personalHrvImpactPct}`,
              count: context.personalSampleCount,
            })}
          </span>
        ) : null}
      </div>

      {context.matchedKeywords.length > 0 ? (
        <div>
          <p className={`font-mono text-[10px] uppercase tracking-[0.15em] ${KILO.text.soft}`}>{t('predictive.preview.flaggedBecause')}</p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {context.matchedKeywords.map((keyword) => (
              <span
                key={keyword}
                className={`rounded-md border border-kilo-border bg-kilo-card px-2 py-0.5 text-xs ${KILO.text.primary}`}
              >
                {keyword}
              </span>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default StressEventPreview
